
import { defineComponent, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import PaymentAdminApi from '@/core/services/payment/PaymentAdminApi'
import { handleApiError } from '@/core/helpers/handleApiError'
import { askConfirm } from '@/core/helpers/askReason'
import { formatMoney } from '@/core/helpers/paymentFormat'
import { payerTypeLabels, productTypeLabels, toOptions } from '@/core/services/payment/labels'
import { Product } from '@/core/services/payment/types'
import { usePaginatedList } from '@/core/composables/usePaginatedList'
import PaymentListLayout from '@/core/components/payment/PaymentListLayout.vue'
import PaymentFormDialog from '@/core/components/payment/PaymentFormDialog.vue'
import { FormField, FormValues } from '@/core/components/payment/formField'

/**
 * Справочник товаров. Ключ товара (тип + единица + количество) неизменяем:
 * при редактировании эти поля скрыты. Удаления нет, только архив.
 */
export default defineComponent({
  name: 'ProductsList',
  components: { PaymentListLayout, PaymentFormDialog },
  setup() {
    const list = usePaginatedList<Product>(PaymentAdminApi.products.list, {
      defaults: { search: '', product_type: '', is_active: '' },
    })
    const formOpen = ref(false)
    const editing = ref<Product | null>(null)
    const initial = ref<FormValues>({})

    const creating = () => editing.value === null
    const unitless = (v: FormValues) => v.product_type === 'company_ownership'

    const fields: FormField[] = [
      { key: 'product_type', label: 'Тип товара', type: 'select', required: true, options: toOptions(productTypeLabels), show: creating },
      {
        key: 'unit', label: 'Единица', type: 'select', required: true,
        options: toOptions({ month: 'Месяц', day: 'День', contact: 'Контакт', rubric: 'Рубрика' }),
        show: v => creating() && !unitless(v),
      },
      { key: 'unit_count', label: 'Количество единиц', type: 'number', min: 1, required: true, show: v => creating() && !unitless(v) },
      { key: 'name', label: 'Название', type: 'text', required: true },
      { key: 'description', label: 'Описание', type: 'textarea' },
      { key: 'price', label: 'Цена, ₽', type: 'number', min: 0.01 },
      { key: 'old_price', label: 'Старая цена, ₽', type: 'number', min: 0.01, hint: 'Необязательно: зачёркнутая цена для показа, должна быть больше цены.' },
      { key: 'discount_percent', label: 'Скидка, %', type: 'number', min: 1, max: 99, hint: 'Необязательно: для показа, на сумму заказа не влияет.' },
      { key: 'payer_types', label: 'Кто может купить', type: 'select', multiple: true, required: true, options: toOptions(payerTypeLabels) },
      { key: 'is_active', label: 'Активен', type: 'switch' },
    ]

    const openCreate = () => {
      editing.value = null
      initial.value = { payer_types: ['individual'], is_active: false }
      formOpen.value = true
    }

    const openEdit = (row: Product) => {
      editing.value = row
      initial.value = {
        name: row.name,
        description: row.description,
        price: row.price === null ? undefined : Number(row.price),
        old_price: row.old_price === null ? undefined : Number(row.old_price),
        discount_percent: row.discount_percent === null ? undefined : row.discount_percent,
        payer_types: row.payer_types,
        is_active: row.is_active,
      }
      formOpen.value = true
    }

    /**
     * При изменении необязательные поля, которые очистили в форме, уходят как null:
     * иначе сервер оставил бы прежнее значение (форма не отправляет пустые поля).
     */
    const save = (payload: Record<string, any>) =>
      editing.value
        ? PaymentAdminApi.products.update(editing.value.id, {
            description: null,
            old_price: null,
            discount_percent: null,
            ...payload,
          })
        : PaymentAdminApi.products.create(payload)

    const archive = async (row: Product) => {
      if (!(await askConfirm('Снять с продажи', `Товар «${row.name}» уйдёт в архив.`, 'В архив'))) {
        return
      }
      try {
        await PaymentAdminApi.products.archive(row.id)
        ElMessage({ type: 'success', message: 'Товар в архиве' })
        await list.load()
      } catch (error) {
        handleApiError(error)
      }
    }

    onMounted(list.init)

    return {
      ...list,
      formOpen, editing, initial, fields,
      openCreate, openEdit, save, archive,
      typeOptions: toOptions(productTypeLabels),
      typeLabels: productTypeLabels,
      payerLabels: payerTypeLabels,
      money: formatMoney,
    }
  },
})
